import React, { useEffect, useId, useRef, useState } from 'react';
import { todayIso } from '../../utils/preferredDate';
import './PreferredDateField.css';

interface PreferredDateFieldProps {
  /** YYYY-MM-DD, or '' for "decide later". */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  laterLabel?: string;
  dateLabel?: string;
  /** Past dates are blocked unless the studio is logging an earlier request. */
  allowPast?: boolean;
  className?: string;
  /**
   * "stacked" (default) puts the choice buttons above the date input.
   * "inline" keeps those options on the same row as the date field.
   */
  layout?: 'stacked' | 'inline';
  /** Tighter controls for multi-day session rows. */
  density?: 'default' | 'compact';
}

/**
 * One control for the preferred shoot date, used on the public contact buttons,
 * in the studio enquiry form and in the quotation builder.
 */
const PreferredDateField = ({
  value,
  onChange,
  label = 'When would you like your shoot?',
  laterLabel = 'Decide later',
  dateLabel = 'I have a date in mind',
  allowPast = false,
  className = '',
  layout = 'stacked',
  density = 'default',
}: PreferredDateFieldProps) => {
  const id = useId();
  const dateRef = useRef<HTMLInputElement>(null);
  const [hasDate, setHasDate] = useState(Boolean(value));
  const rootClass = [
    'preferred-date',
    layout === 'inline' ? 'preferred-date-inline' : '',
    density === 'compact' ? 'preferred-date-compact' : '',
    className,
  ].filter(Boolean).join(' ');

  // A value arriving from outside (opening a saved draft) switches the choice to match it.
  useEffect(() => {
    if (value) setHasDate(true);
  }, [value]);

  const choose = (next: boolean) => {
    setHasDate(next);
    if (!next) onChange('');
    else dateRef.current?.focus();
  };

  const dateInput = (
    <div className="preferred-date-input">
      <input
        ref={dateRef}
        id={`${id}-date`}
        className={value ? '' : 'is-empty'}
        type="date"
        aria-label="Preferred date"
        autoComplete="off"
        value={value}
        min={allowPast ? undefined : todayIso()}
        onFocus={() => setHasDate(true)}
        onChange={(event) => {
          setHasDate(true);
          onChange(event.target.value);
        }}
      />
    </div>
  );

  const choices = (
    <div className="preferred-date-choice" role="radiogroup" aria-label={label}>
      <button
        type="button"
        role="radio"
        aria-checked={hasDate}
        className={hasDate ? 'is-selected' : ''}
        onClick={() => choose(true)}
      >
        {dateLabel}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={!hasDate}
        className={!hasDate ? 'is-selected' : ''}
        onClick={() => choose(false)}
      >
        {laterLabel}
      </button>
    </div>
  );

  if (layout === 'inline') {
    return (
      <fieldset className={rootClass}>
        <legend>{label}</legend>
        <div className="preferred-date-row">
          {dateInput}
          {choices}
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset className={rootClass}>
      <legend>{label}</legend>
      {choices}
      {hasDate ? dateInput : null}
    </fieldset>
  );
};

export default PreferredDateField;
